import { type PendingAttachmentFile, pendingThreadAttachmentsAtom } from "@/atoms/messagesAtoms.ts";
import { Loader } from "@/components/loader/Loader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { Paperclip } from "lucide-react";
import { useMemo } from "react";

const statusText = (file: PendingAttachmentFile): string => {
  switch (file.status) {
    case "uploading":
      return "Uploading…";
    case "analyzing":
      return "Analyzing…";
    case "error":
      return file.errorMessage ?? "Failed";
  }
};

/**
 * Provisional attachment chips for an in-flight send, shown on the user message
 * until the real file artifacts arrive (see `pendingThreadAttachmentsAtom`).
 * Renders only for the message whose `clientChatId` matches the overlay's
 * `tempClientId`, so it follows the message through socket reconciliation.
 */
export const PendingAttachmentChips = ({
  threadId,
  clientChatId,
}: {
  threadId: number;
  clientChatId?: number | null;
}) => {
  const pending = useAtomValue(
    useMemo(() => selectAtom(pendingThreadAttachmentsAtom, (map) => map[threadId]), [threadId]),
  );

  if (!pending || clientChatId == null || pending.tempClientId !== clientChatId) {
    return null;
  }

  const hasError = pending.files.some((f) => f.status === "error");

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex flex-wrap justify-end gap-2">
        {pending.files.map((file) => (
          <div
            key={file.id}
            className={cn(
              "flex items-center gap-2 p-2 rounded-lg border bg-muted/50",
              file.status === "error" ? "border-destructive" : "opacity-80",
            )}
          >
            {file.preview ? (
              <img src={file.preview} alt={file.name} className="h-12 w-12 rounded object-cover" />
            ) : (
              <div className="h-12 w-12 rounded bg-muted flex items-center justify-center text-muted-foreground">
                <Paperclip className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{file.name}</p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {(file.status === "uploading" || file.status === "analyzing") && (
                  <Loader size="xs" />
                )}
                <span className={cn(file.status === "error" && "text-destructive")}>
                  {statusText(file)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {hasError && pending.retry && (
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={pending.retry}
          data-statsig-name="pending-attachment-retry"
        >
          Retry
        </Button>
      )}
    </div>
  );
};
